import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { get as _get } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { delay, filter, first, map, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import {
  GracePeriodnotificationService,
  NotificationRequest,
  NotificationRequestInternalEmail
} from '@amfam/policy/add-vehicle/ui';
import { PolicySelectors } from '@amfam/policy/data-access';
import {
  ANSRequestModel,
  AutoPolicy,
  GracePeriodDays,
  Policy,
  PolicyAnalytics,
  ReplaceVehicleANSNotificationType,
  ReplaceVehicleCustomerTemplateIdCode,
  RiskSelectionContext
} from '@amfam/policy/models';
import { PrettyPolicyNum } from '@amfam/policy/pipes';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { userQuery } from '@amfam/shared/user';
import { Agent, agentQuery, fromAgentActions, initialAgent } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { CopyService, UtilService } from '@amfam/shared/utility/shared-services';
import { DockingBarService, LoadingSpinnerService, PageTitleService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import * as replaceVehicleActions from '../../../core/store/replace-vehicle/replace-vehicle.actions';

@Component({
  selector: 'ds-replace-vehicle-grace-period',
  templateUrl: './replace-vehicle-grace-period.component.html',
  styleUrls: ['./replace-vehicle-grace-period.component.scss'],
  standalone: false
})
export class ReplaceVehicleGracePeriodComponent implements OnInit, AfterViewInit, OnDestroy {
  public gracePeriodDays$: Observable<string>;
  isPartner$ = this.store.select(BrandSelectors.selectIsPartner);
  isAdvancePolicy$ = this.store.select(PolicySelectors.selectHasAdvancePolicies);
  public producers: Agent[] = [];
  public selectedAgent: Agent;

  public agentNotificationForm: UntypedFormGroup;
  public selectAgent: AbstractControl;
  public policy: AutoPolicy;
  public disableNotifyAgentBtn = false;
  public showErrorMessage = false;
  public autoWhatIfError: Observable<boolean>;
  multipleAutoPolicies = true;
  regularGracePeriodDays = GracePeriodDays.REGULAR;
  public showOverviewLink = true;
  descriptionType: string;

  private emailAddress: string;
  private firstName: string;
  private lastName: string;
  private policyList: Policy[];
  private stop$: Subject<void> = new Subject<void>();
  private vehicleId: string;
  private riskSelectionContextType: RiskSelectionContext;
  private isNewCarReplaceCoverage: boolean;

  constructor(
    private store: Store<fromRoot.RootState>,
    private copyService: CopyService,
    private spinner: LoadingSpinnerService,
    private gracePeriodNotificationService: GracePeriodnotificationService,
    private formBuilder: UntypedFormBuilder,
    public roleService: ImpersonateRolesService,
    private route: ActivatedRoute,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    private analyticsFacade: AnalyticsFacade,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    const producerIds = new Array<string>();

    this.pageTitle.set('Replace a Vehicle');
    this.dockingBarService.registerHeading('Replace a Vehicle');

    this.multipleAutoPolicies = !!this.route.snapshot.data['multiplePolicies'];
    /**
     * AS: Filter out the agents who are associated with the auto policies.
     */
    combineLatest(
      this.route.params,
      this.store.select(PolicySelectors.selectVehiclePolicies),
      this.store.select(PolicySelectors.selectAutoPolicyRisks),
      this.store.select(userQuery.selectUserState),
      this.store.select(agentQuery.getAgentEntities),
      this.store.select(fromRoot.getVehicleRiskSelectionContextType),
      this.store.select(fromRoot.hasAdvPolicyAndNewCarReplaceCoverage),
      (
        params,
        policies,
        risks,
        user,
        agents,
        riskSelectionContextType,
        isNewCarReplaceCoverage
      ) => ({
        vehicleId: params['id'],
        policies: policies,
        risks: risks,
        user: user,
        agents: agents,
        riskSelectionContextType: riskSelectionContextType,
        isNewCarReplaceCoverage: isNewCarReplaceCoverage
      })
    )
      .pipe(takeUntil(this.stop$))
      .subscribe(
        res => {
          this.policyList = res.policies;
          this.vehicleId = res.vehicleId;
          this.riskSelectionContextType = res.riskSelectionContextType;
          this.isNewCarReplaceCoverage = res.isNewCarReplaceCoverage;

          if (RiskSelectionContext.REPLACE_VEHICLE === this.riskSelectionContextType) {
            this.descriptionType = 'offers';
            this.showOverviewLink = false;
          } else {
            this.descriptionType = 'includes';
          }

          const matchingVehicle = res.risks.find(vehicle => _get(vehicle, 'id') === this.vehicleId);
          if (matchingVehicle) {
            this.policy = res.policies.find(
              policy => _get(policy, 'policyNumber') === matchingVehicle.policyNumber
            );
          }
          this.policyList.forEach(policy => {
            // JP: Check if we've loaded producer already - dispatch load if not
            const producerId = _get(policy, 'assignedProducer.producerIdNum');
            if (!res.agents[producerId] && !producerIds.includes(producerId)) {
              producerIds.push(producerId);
              this.store.dispatch(
                new fromAgentActions.LoadAgentAction(
                  Object.assign({}, initialAgent, {
                    id: producerId
                  })
                )
              );
            }
          });
          if (res.user !== null && res.user.customerId) {
            if (res.user.firstName) {
              this.firstName = res.user.firstName;
            }
            if (res.user.lastName) {
              this.lastName = res.user.lastName;
            }
            if (res.user.emailAddress) {
              this.emailAddress = res.user.emailAddress;
            }
          }
        },
        err => {
          /**
           * AS: Log it somewhere.
           */
        }
      );

    this.autoWhatIfError = this.store.select(fromRoot.getReplaceVehicleHasError);

    // JP: Grab all agents that have an associated policy
    combineLatest(
      this.store.select(fromRoot.getAllActiveAgents),
      this.store.select(agentQuery.getAreAllAgentsLoaded),
      (agents, loaded) => ({ agents: agents, loaded: loaded })
    )
      .pipe(
        filter(data => data.loaded),
        takeUntil(this.stop$)
      )
      .subscribe(data => {
        this.producers = data.agents;
      });

    this.gracePeriodDays$ = this.store.select(userQuery.selectUserStateCode).pipe(
      withLatestFrom(this.isPartner$, this.isAdvancePolicy$),
      takeUntil(this.stop$),
      map(([userState, isPartner, isAdvancePolicy]) => {
        const isExtendedGracePeriodState = !!this.copyService
          .getCopy('policy.extendedGracePeriodStates')
          .find(state => state === userState);
        if (isPartner)
          return !isExtendedGracePeriodState ? GracePeriodDays.REGULAR : GracePeriodDays.EXTENDED;
        else {
          return isAdvancePolicy ? GracePeriodDays.REGULAR : GracePeriodDays.EXTENDED;
        }
      })
    );

    this.buildForm();
  }

  ngAfterViewInit() {
    if (this.multipleAutoPolicies) {
      this.analyticsFacade.trackPage(
        PolicyAnalytics.pagePolicyAddVehicleNewOrReplaceMultiAdvGraceNotification
      );
      setTimeout(() => {
        this.analyticsFacade.trackPage(PolicyAnalytics.pageAddVehicleMultiAdvPolicies);
      });
    } else if (this.riskSelectionContextType === RiskSelectionContext.REPLACE_VEHICLE) {
      const data = this.isNewCarReplaceCoverage
        ? PolicyAnalytics.pagePolicyNewCarReplaceGraceNotice
        : PolicyAnalytics.pagePolicyReplaceVehiclePolicyGracePeriod;
      this.analyticsFacade.trackPage(data);
    } else {
      this.analyticsFacade.trackPage(PolicyAnalytics.pagePolicyAddVehicleGraceNotification);
    }
    this.store
      .select(PolicySelectors.selectHasClassicPolicies)
      .pipe(
        withLatestFrom(
          this.isPartner$,
          this.store.select(PolicySelectors.selectHasAdvancePolicies)
        ),
        first(),
        delay(0),
        tap(([hasClassicPolicy, isPartner, advancePolicy]) => {
          if (hasClassicPolicy && !advancePolicy && !isPartner)
            this.analyticsFacade.trackPage(PolicyAnalytics.hardStopForClassicPolicy);
        })
      )
      .subscribe();

    this.store
      .select(fromRoot.hasAnyPolicyCompAndCollisonCoverageAndCarLightTruck)
      .pipe(
        withLatestFrom(
          this.isPartner$,
          this.store.select(PolicySelectors.selectHasClassicPolicies),
          this.store.select(PolicySelectors.selectClassicPolicies),
          this.store.select(fromRoot.hasAnyPolicyBASCompAndCollisonCoverage)
        ),
        first(),
        delay(0),
        tap(
          ([
            hasAnyPolicyCompAndCollisonCoverage,
            isPartner,
            hasClassicPolicies,
            classicPolicies,
            BASCompAndCollisonCoverage
          ]) => {
            if (
              hasClassicPolicies &&
              !hasAnyPolicyCompAndCollisonCoverage &&
              !isPartner &&
              !(classicPolicies.length && BASCompAndCollisonCoverage)
            )
              this.analyticsFacade.trackPage(PolicyAnalytics.pagePolicyHardStopLiabilityOnly);
          }
        )
      )
      .subscribe();
  }

  buildForm(): void {
    this.agentNotificationForm = this.formBuilder.group({
      selectAgent: ['', Validators.compose([Validators.required])]
    });

    this.selectAgent = this.agentNotificationForm.controls['selectAgent'];
  }

  submit(): void {
    const isContextTypeReplaceVehicle =
      this.riskSelectionContextType === RiskSelectionContext.REPLACE_VEHICLE;
    this.store.dispatch(
      new replaceVehicleActions.SendAnalyticsAction({
        buttonAnalyticName: 'PolicyGracePeriodNotifyAgent'
      })
    );
    this.analyticsFacade.trackEvent({
      event: 'agent_referral',
      form_name: 'Replace Vehicle'
    });
    this.spinner.start();
    this.disableNotifyAgentBtn = true;
    if (this.producers.length > 1) {
      this.selectedAgent = this.producers.find(producer => this.selectAgent.value === producer.id);
    } else if (!this.selectedAgent) {
      this.selectedAgent = this.producers[0];
    }

    const policyList = this.policy
      ? [this.policy]
      : this.policyList.filter(
          policy => policy.assignedProducer.producerIdNum === this.selectedAgent.id
        );

    const content = this.copyService.getCopy('policy.replaceVehicleAgentEmailContent', {
      producer: this.selectedAgent,
      model: { firstName: this.firstName, lastName: this.lastName },
      policyNumber: policyList.map(p => new PrettyPolicyNum().transform(p)),
      plural: policyList.length > 1 ? 's' : '',
      existingVehicle: '',
      existingVehicleVin: '',
      newVehicle: '',
      newVehicleVin: '',
      msrp: 'N/A',
      estimatedVehiclePrice: 'N/A'
    });

    const notificationRequest: NotificationRequest = {
      producer: this.selectedAgent,
      policyList,
      firstName: this.firstName,
      lastName: this.lastName,
      email: this.emailAddress,
      subject: isContextTypeReplaceVehicle
        ? this.copyService.getCopy('policy.replaceVehicleGreacePeriodInternalEmailMessageSubject')
        : '',
      content: isContextTypeReplaceVehicle ? content : ''
    };
    const internalEmail = new NotificationRequestInternalEmail(notificationRequest);

    this.gracePeriodNotificationService
      .generateInternalEmails(internalEmail)
      .pipe(takeUntil(this.stop$))
      .subscribe(
        res => {
          this.spinner.stop();
          this.disableNotifyAgentBtn = false;
          if (!this.vehicleId) {
            this.store.dispatch(
              fromRouterActions.Go({
                path: ['/policies/risk-selection/confirmation']
              })
            );
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: ['/policies/replace-vehicle/' + this.vehicleId + '/confirmation']
              })
            );
          }
        },
        err => {
          this.spinner.stop();
          this.disableNotifyAgentBtn = false;
          this.showErrorMessage = true;
        }
      );

    const policyNumbers = this.policy
      ? [this.policy.policyNumber]
      : this.policyList
          .filter(policy => policy.assignedProducer.producerIdNum === this.selectedAgent.id)
          .map(p => p.policyNumber);

    const conentPath = isContextTypeReplaceVehicle
      ? 'policy.replaceVehicleSendToAgentANSMessage'
      : 'policy.addVehicleSendToAgentANSMessage';

    const ansContent = this.copyService.getCopy(conentPath, {
      producer: internalEmail.producer,
      model: internalEmail,
      policyNumber: policyNumbers.map(p => new PrettyPolicyNum().transform(p)),
      plural: policyNumbers.length > 1 ? 's' : '',
      existingVehicle: '',
      existingVehicleVin: '',
      newVehicle: '',
      newVehicleVin: '',
      msrp: 'N/A',
      estimatedVehiclePrice: 'N/A'
    });
    const payload: ANSRequestModel = new ANSRequestModel({
      producer: this.selectedAgent,
      orderNumber: 'NA',
      policyNumber: policyNumbers.length > 1 ? policyNumbers.join(',') : policyNumbers[0],
      from: this.copyService.getCopy('shared.myAccountTxt'),
      customerName: this.firstName + ' ' + this.lastName,
      notificationText: ansContent,
      vehicleId: this.vehicleId,
      notificationType: ReplaceVehicleANSNotificationType
    });

    this.sendCustomerEmail(policyNumbers);

    // Send ANS Notification
    this.store.dispatch(new replaceVehicleActions.ReplaceVehicleANSNotifyAgentAction(payload));
  }

  sendCustomerEmail(policyNumbers?: string[]) {
    const policyNumberString = policyNumbers
      ? policyNumbers.length > 1
        ? policyNumbers.map(p => this.utilService.maskPolicyNumber(p)).join(',')
        : this.utilService.maskPolicyNumber(policyNumbers[0])
      : null;

    this.store
      .select(userQuery.selectFirstName)
      .pipe(
        withLatestFrom(
          this.store.select(userQuery.selectMyAccountEmail),
          this.store.select(userQuery.selectPrimaryEmail),
          this.store.select(BrandSelectors.selectPartnerId)
        ),
        first(),
        tap(([customerName, accountEmail, primaryEmail, partnerId]) => {
          const customerEmailPayload = this.gracePeriodNotificationService.generateTemplateEmail(
            primaryEmail,
            accountEmail,
            customerName,
            this.selectedAgent?.agentId,
            partnerId,
            this.riskSelectionContextType === RiskSelectionContext.REPLACE_VEHICLE
              ? ReplaceVehicleCustomerTemplateIdCode
              : '',
            policyNumberString
          );
          this.store.dispatch(
            new replaceVehicleActions.SendGracePeriodCustomerEmail(customerEmailPayload)
          );
        })
      )
      .subscribe();
  }

  getAgentURL() {
    if (_get(this.selectedAgent, 'websiteUrl')) {
      return `${this.selectedAgent.websiteUrl}contact-us/#contact-form`;
    }
    return '';
  }

  getPhoneNumber(phoneNumber) {
    return 'tel:+1' + phoneNumber;
  }

  ngOnDestroy(): void {
    this.spinner.stop();
    this.store.dispatch(new replaceVehicleActions.SetVehicleRiskSelectionContext(null));
    this.stop$.next();
    this.stop$.complete();
  }
}
